import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudit } from '../../api/hooks';
import type { AuditEntry, AuditFilter } from '../../api/types';
import { ErrorState, Loading } from '../../components/States';
import { fmtTime } from '../../lib/dates';

const FILTERS: AuditFilter[] = ['all', 'support', 'users', 'orgs'];

/** Детали записи журнала, если причины нет: «ключ: значение; …». */
export function auditDetails(details: Record<string, unknown>): string {
  return Object.entries(details ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(
      ([k, v]) =>
        `${k}: ${Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}`,
    )
    .join('; ');
}

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<AuditFilter>('all');
  const audit = useAudit(filter);
  const rows = audit.data?.pages.flatMap((p) => p.items) ?? [];

  const action = (a: AuditEntry) =>
    i18n.exists(`audit.actions.${a.action}`) ? t(`audit.actions.${a.action}`) : a.action;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('admin.audit.title')}</h1>
          <p>{t('admin.audit.sub')}</p>
        </div>
      </div>
      <div className="toolbar">
        <div className="chips" role="group" aria-label={t('admin.audit.filters')}>
          {FILTERS.map((k) => (
            <button
              key={k}
              type="button"
              className="chip"
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
            >
              {t(`admin.audit.chips.${k}`)}
            </button>
          ))}
        </div>
      </div>
      {audit.isLoading ? (
        <Loading />
      ) : audit.error && !audit.data ? (
        <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />
      ) : (
        <div className="table-wrap plain">
          <table>
            <thead>
              <tr>
                <th>{t('admin.audit.cols.time')}</th>
                <th>{t('admin.audit.cols.who')}</th>
                <th>{t('admin.audit.cols.action')}</th>
                <th>{t('admin.audit.cols.target')}</th>
                <th>{t('admin.audit.cols.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((a) => {
                  const extra = a.reason || auditDetails(a.details);
                  return (
                    <tr key={a.id}>
                      <td className="small muted" style={{ whiteSpace: 'nowrap' }}>
                        {fmtTime(a.created_at)}
                      </td>
                      <td>
                        {a.actor?.name ?? t('admin.audit.system')}
                        {a.on_behalf_of && (
                          <div className="muted small">
                            {t('admin.audit.onBehalf', { name: a.on_behalf_of.name })}
                          </div>
                        )}
                      </td>
                      <td>{action(a)}</td>
                      <td>{a.target_label ?? '—'}</td>
                      <td className="small">{extra || <span className="muted">—</span>}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="empty">
                    {t('admin.audit.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {audit.hasNextPage && (
            <div className="more">
              <button
                type="button"
                className="btn"
                disabled={audit.isFetchingNextPage}
                onClick={() => void audit.fetchNextPage()}
              >
                {t('common.showMore')}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
